import { Button, Menu, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import { createStyles, makeStyles, Theme, } from '@material-ui/core';
import React from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { LineupApiResponse } from '../../preContest/queries/queryTypes';
import { APIPagination, AuthResponse, Filters, Pages } from '../../preContest/store/types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocalStorage } from 'usehooks-ts';
import { GridValidRowModel, useGridApiContext } from '@mui/x-data-grid';
import { APIState } from '../../utils/api/reducer';
import LockIcon, { UpgradeModal } from '../LockIcon';
import { SlateResponse } from '../store/types';
import { findLineupsToFavorite } from './utils';
import { setUniqueLimit } from '../store/actions';
import { IRootState } from '../../../store/reducer';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const useStyles = makeStyles((theme: Theme) => createStyles({
  quickFavorite: {
    '& .MuiButton-root': {
      color: 'white',
      border: '1px solid rgba(1, 90, 206, 1)',
      padding: '4px 8px',
    },
    '& .MuiListItem-root.Mui-disabled': {
      display: 'flex',
      justifyContent: 'space-between',
    }
  },
  disabled: {
    color: "#8F9296",
    cursor: 'not-allowed',
    '& svg': {
      color: "#8F9296",
    }
  },
})
);

interface OwnProps {
  readonly disabled: boolean;
};

interface ReduxProps {
  readonly selectedSlate: SlateResponse;
  readonly filters: Filters,
  readonly pageInfo: Pages,
  readonly pagination: APIPagination;
  readonly authenticated: APIState<AuthResponse>;
  readonly stackExposureDict: { [key: string]: number };
  readonly playerExposureDict: { [key: string]: number };
}

type Props = OwnProps & ReduxProps;

const QuickFavorite = (props: Props) => {
  const classes = useStyles();
  const { selectedSlate, disabled, stackExposureDict, playerExposureDict } = props;
  const apiRef = useGridApiContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dispatch = useDispatch();
  const [customTotalFavorites, setCustomTotalFavorites] = React.useState('');
  const [uniquePlayersCount, setUniquePlayersCount] = React.useState('');
  const [useCustomNumber, setUseCustomNumber] = React.useState(false);
  const [savedLineups, setSavedLineups] = useLocalStorage<(LineupApiResponse | GridValidRowModel)[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );
  const [openModal, setOpenModal] = React.useState(false);
  const { uniqueLimit: uniqueLimitPrev } = useSelector((state: IRootState) => state.common);

  const uniqueLimit = selectedSlate.lineupRules.positions.split(',').length - 1 || 8;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async (pageCount: number) => {
    if (pageCount > 0) {
      const saveableLineups = findLineupsToFavorite(playerExposureDict, stackExposureDict, savedLineups, apiRef, selectedSlate, pageCount, uniquePlayersCount);
      setSavedLineups(saveableLineups);
    } else {
      setSavedLineups([]);
    }
    setAnchorEl(null);
  };

  const handleCustomNumberChange = (number: string) => {
    const newValue = Number(number);
    const limit = 10000;
    if ((Number.isInteger(newValue) && !Number.isNaN(newValue) && newValue >= 1 && newValue <= limit) || number === '') {
      setCustomTotalFavorites(number);
    }
  }
  const handleUniqueCountChange = (number: string) => {
    const newValue = Number(number);
    if ((Number.isInteger(newValue) && !Number.isNaN(newValue) && newValue >= 0 && newValue <= uniqueLimit) || number === '') {
      dispatch(setUniqueLimit(newValue));
      setUniquePlayersCount(number);
    }
  }

  const tierLocked = false;

  React.useEffect(() => {
    setUniquePlayersCount(uniqueLimitPrev);
  }, [uniqueLimitPrev]);
  return (
    <div className={classes.quickFavorite}>
      <UpgradeModal open={openModal} onClose={() => setOpenModal(false)} />
      <Button aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} disabled={disabled}>
        Quick Favorite
        <ArrowDropDownIcon />
      </Button>
      <Menu
        id="simple-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem>
          <Stack direction='row' spacing={0.5} alignItems='center' columnGap='8px'>
            <Tooltip title='Number of unique players between each lineup in your favorited lineups'>
              <Stack><InfoOutlinedIcon /></Stack>
            </Tooltip>
            <Stack alignItems='center'>
              <Stack>
                Uniques:
              </Stack>
              <Stack fontSize='10px'>
                (Max {uniqueLimit})
              </Stack>
            </Stack>
            <TextField
              sx={{ width: 100 }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              size='small'
              type='number'
              value={uniquePlayersCount}
              onChange={(e) => handleUniqueCountChange(e.target.value)}
            />
          </Stack>
        </MenuItem>
        <MenuItem onClick={() => handleClose(300)} className={tierLocked ? classes.disabled : ''} style={{ display: 'flex', justifyContent: 'space-between' }}>Favorite Top 300 Lineups {tierLocked && <LockIcon />}</MenuItem>
        <MenuItem onClick={() => handleClose(150)} className={tierLocked ? classes.disabled : ''} style={{ display: 'flex', justifyContent: 'space-between' }}>Favorite Top 150 Lineups {tierLocked && <LockIcon />}</MenuItem>
        <MenuItem onClick={() => handleClose(100)} className={tierLocked ? classes.disabled : ''} style={{ display: 'flex', justifyContent: 'space-between' }}>Favorite Top 100 Lineups{tierLocked && <LockIcon />}</MenuItem>
        <MenuItem onClick={() => handleClose(50)} className={tierLocked ? classes.disabled : ''} style={{ display: 'flex', justifyContent: 'space-between' }}>Favorite Top 50 Lineups {tierLocked && <LockIcon />}</MenuItem>
        <MenuItem onClick={() => handleClose(20)}>Favorite Top 20 Lineups</MenuItem>
        <MenuItem onClick={() => setUseCustomNumber(true)}>
          {useCustomNumber ?
            <Stack direction='row' spacing={0.5}>
              <TextField
                sx={{ width: 100 }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                size='small'
                type='number'
                value={customTotalFavorites}
                onChange={(e) => handleCustomNumberChange(e.target.value)}
              />
              <Button color='primary' variant='contained' disabled={customTotalFavorites === ''} onClick={() => handleClose(Number(customTotalFavorites))}>Done</Button>
            </Stack> :
            <>Favorite Custom Amount</>
          }
        </MenuItem>
        <MenuItem onClick={() => handleClose(0)}>Un-Favorite All</MenuItem>
      </Menu>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  const {
    filters,
    pageInfo,
    pagination,
  } = state.preContest;
  const {
    authenticated,
    stackExposureDict,
    playerExposureDict,
    selectedSlate,
  } = state.common;
  return {
    selectedSlate,
    filters,
    pageInfo,
    pagination,
    authenticated,
    stackExposureDict,
    playerExposureDict,
  }
};

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(QuickFavorite);